import React, { useMemo } from 'react';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { Plus } from 'lucide-react';

import { FormFieldSet, InputField, SwitchField, TextareaField } from '@epoch-ui/components/Form';
import { Button } from '@epoch-ui/components/Button/button';
import CloseIcon from 'components/common/icons/CloseIcon';

import { SingleChoiceFormInput } from '../schemas/SingleChoiceSchema';

const MAX_POSSIBLE_OPTIONS = 10;

interface SingleChoiceFormProps {
  disabled?: boolean;
}

export const SingleChoiceForm: React.FC<SingleChoiceFormProps> = ({ disabled = false }) => {
  const { register, control, setValue } = useFormContext<SingleChoiceFormInput>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options'
  });

  const hasOtherOption = useWatch({ control, name: 'hasOtherOption' });
  const optionsCount = useMemo(
    () => fields.length + (hasOtherOption ? 1 : 0),
    [fields.length, hasOtherOption]
  );

  return (
    <div className="tw-flex tw-flex-grow tw-flex-col">
      <div className="tw-mb-6">
        <div className="tw-mb-[2px] tw-text-xl tw-font-medium tw-leading-7 tw-text-black">
          Single choice
        </div>
        <span className="tw-text-sm tw-font-normal tw-text-primary-grey">
          Collect one response out of multiple options
        </span>
      </div>

      <FormFieldSet className="tw-gap-0">
        {/* Question prompt */}
        <TextareaField
          {...register('label')}
          label="Question"
          disabled={disabled}
          className="tw-mb-6 tw-mt-0"
        />

        {/* Options */}
        <>
          <span className="tw-mb-3 tw-font-medium tw-text-dark-black">Options</span>
          <div className="border-left tw-pl-4">
            <span className="tw-mb-2 tw-text-xs tw-text-primary-grey">
              Only 1 option can be selected
            </span>

            {fields.map((field, index): React.JSX.Element => {
              return (
                <div key={field.id} className="tw-mt-3">
                  <div className="tw-mb-0 tw-flex tw-items-center tw-gap-3">
                    <InputField
                      {...register(`options.${index}.value`)}
                      label=""
                      placeholder={`Option ${index + 1}`}
                      disabled={disabled}
                      className="tw-mt-0 tw-w-full"
                      inputClassName="tw-mt-0"
                      data-testid={`option-${index}-input`}
                    />
                    {fields.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={(): void => remove(index)}
                        className="tw-h-auto tw-p-0 tw-text-gray-600"
                        disabled={disabled}
                      >
                        <CloseIcon />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}

            {/* "Other" option */}
            {hasOtherOption && (
              <div className="tw-mb-0 tw-mt-3 tw-flex tw-items-center tw-gap-3">
                <InputField
                  name=""
                  label=""
                  placeholder="Other"
                  disabled
                  className="tw-mt-0 tw-w-full"
                  inputClassName="tw-mt-0"
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={(): void => setValue('hasOtherOption', !hasOtherOption)}
                  className="tw-h-auto tw-p-0 tw-text-gray-600"
                  disabled={disabled}
                >
                  <CloseIcon />
                </Button>
              </div>
            )}

            {/* Buttons to add more or "Other" */}
            {optionsCount < MAX_POSSIBLE_OPTIONS && (
              <div className="tw-mt-4 tw-flex tw-items-center tw-font-medium">
                <Button
                  variant="link"
                  onClick={(): void => append({ value: '' })}
                  className="tw-flex tw-h-auto tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-primary tw-no-underline"
                  disabled={disabled}
                >
                  <Plus className="tw-h-4 tw-w-4 tw-text-primary-blue" />
                  <span>Add option</span>
                </Button>

                {!hasOtherOption && (
                  <>
                    <span>&nbsp;or&nbsp;</span>
                    <Button
                      variant="link"
                      onClick={(): void => setValue('hasOtherOption', !hasOtherOption)}
                      className="tw-flex tw-h-auto tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-primary tw-no-underline"
                      disabled={disabled}
                    >
                      <span>add &quot;Other&quot;</span>
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </>

        {/* Required toggle */}
        <SwitchField
          {...register('isRequired')}
          label="Required"
          disabled={disabled}
          className="tw-mt-4"
        />
      </FormFieldSet>
    </div>
  );
};
